import React, { useEffect, useState } from 'react';

/**
 * TapToPayScreen — wrapper around the native Stripe Terminal SDK Tap to
 * Pay flow. The actual "Hold card near top of phone" UI is rendered
 * NATIVELY by the iOS Stripe Terminal SDK (via the entitled system
 * sheet), not by this React component.
 *
 * This screen renders:
 *   - A connecting/processing state while the SDK initialises
 *   - An error message if connect/process fails
 *   - A back button to escape if the user cancels
 *
 * The native sheet is presented automatically when collectPaymentMethod
 * is called. The user taps their card on the back of the iPhone, the
 * sheet dismisses, and onPaymentComplete fires.
 */
export default function TapToPayScreen({ amount, terminal, locationId, onBack, onPaymentComplete, currencySymbol = '£' }) {
  const [phase, setPhase] = useState('connecting'); // connecting | tapping | error
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Step 1: connect to the Tap to Pay reader (the iPhone itself)
        const connected = await terminal.connect(locationId);
        if (cancelled) return;

        if (!connected) {
          setPhase('error');
          setErrorMessage(terminal.error || 'Could not connect Tap to Pay');
          return;
        }

        // Step 2: trigger the native sheet (delegated to caller via
        // onPaymentComplete prop — caller sets up the paymentIntent and
        // calls terminal.processPayment which presents the system UI).
        setPhase('tapping');
        if (onPaymentComplete) {
          await onPaymentComplete();
        }
      } catch (e) {
        if (cancelled) return;
        setPhase('error');
        // The plugin's error shape is { message, errorMessage, data: { message, declineCode, code } }
        // Prefer the declineCode-aware data.message when present (more user-readable for card declines).
        const msg = e?.data?.message || e?.message || e?.errorMessage || 'Payment failed';
        setErrorMessage(msg);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the terminal hook surfaces an error after the initial render
  // (e.g. user tapped a card and Stripe declined), reflect it in the UI.
  useEffect(() => {
    if (terminal.error && phase !== 'error') {
      setPhase('error');
      setErrorMessage(terminal.error);
    }
  }, [terminal.error, phase]);

  return (
    <div className="co-screen-content" style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', color: '#737373', marginBottom: '8px' }}>Tap to Pay on iPhone</div>
        <div style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a' }}>{currencySymbol}{(amount || 0).toFixed(2)}</div>
      </div>

      {phase === 'connecting' && (
        <div style={{ textAlign: 'center', color: '#737373' }}>
          <div style={{ fontSize: '14px', marginBottom: '8px' }}>Connecting…</div>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>Preparing your iPhone to accept contactless payment</div>
        </div>
      )}

      {phase === 'tapping' && (
        <div style={{ textAlign: 'center', color: '#737373' }}>
          <div style={{ fontSize: '14px', marginBottom: '8px' }}>Ready</div>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>Hold the customer's card or contactless device near the top of your iPhone</div>
        </div>
      )}

      {phase === 'error' && (
        <div style={{ textAlign: 'center', color: '#ef4444', marginTop: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Error</div>
          <div style={{ fontSize: '12px' }}>{errorMessage}</div>
        </div>
      )}

      <button
        onClick={onBack}
        disabled={terminal.isProcessing}
        style={{ marginTop: '32px', padding: '12px 24px', backgroundColor: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', cursor: terminal.isProcessing ? 'not-allowed' : 'pointer', fontSize: '14px', color: '#1a1a1a' }}
      >
        Cancel
      </button>
    </div>
  );
}
